import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { DelayCategory } from "../models/DelayCategory";
import { getDelayCategories } from "../repositories/DelayCategoryRepository";

const DelayCategoryTag = ({
	backgroundColor,
	delayCategoryCode,
}: {
	backgroundColor?: string;
	delayCategoryCode?: string;
}) => {
	return (
		<span
			style={{
				backgroundColor: backgroundColor || "#ccc",
				width: "25px",
				height: "25px",
				borderRadius: "4px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: "#fff",
				fontWeight: "bold",
				marginRight: "8px",
				marginLeft: "8px",
			}}
		>
			{delayCategoryCode}
		</span>
	);
};

type Props = {
	label: string;
	value: number | null;
	onChange?: Function;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	assetId?: number | null;
	required?: boolean;
};

const useDelayCategories = (
	assetId?: number | null,
	onError?: (error: Error) => void,
) => {
	return useQuery<DelayCategory[], Error>({
		queryKey: ["delayCategories", assetId],
		queryFn: () => getDelayCategories(assetId),
		onError: onError,
	});
};

export const DelayCategorySelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	assetId,
	required = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: delayCategories, isLoading } = useDelayCategories(
		assetId,
		(error) => setError(error.message),
	);

	const selectedDelayCategory =
		delayCategories?.find(
			(delayCategory) => delayCategory.DelayCategoryId === value,
		) || null;

	return (
		<>
			<Autocomplete
				id="clear-on-escape"
				clearOnEscape
				options={delayCategories || []}
				getOptionLabel={(option) => option.DelayCategoryName}
				disabled={disabled}
				renderOption={(props, option) => (
					<Box
						{...props}
						component="li"
						sx={{
							display: "flex",
							alignItems: "center",
						}}
					>
						<DelayCategoryTag
							backgroundColor={option.DelayCategoryColor}
							delayCategoryCode={option?.DelayCategoryName.charAt(
								0,
							).toUpperCase()}
						/>
						{option.DelayCategoryName}
					</Box>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message}
						InputProps={{
							...params.InputProps,
							startAdornment: value ? (
								<DelayCategoryTag
									backgroundColor={selectedDelayCategory?.DelayCategoryColor}
									delayCategoryCode={selectedDelayCategory?.DelayCategoryName.charAt(
										0,
									).toUpperCase()}
								/>
							) : null,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedDelayCategory}
				onChange={(event, newValue) => {
					onChange(newValue?.DelayCategoryId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading delay categories"
			/>
		</>
	);
};
